<template>
  <div class="training-management">
    <!-- 搜索和操作栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <el-input v-model="searchForm.title" placeholder="搜索培训标题" style="width: 300px" @keyup.enter="handleSearch">
          <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
        </el-input>
        <el-date-picker v-model="createTimeRange" type="daterange" value-format="yyyy-MM-dd" format="yyyy-MM-dd"
          range-separator="至" start-placeholder="创建开始" end-placeholder="创建结束" clearable unlink-panels
          style="width: 260px; margin-left: 10px" @change="handleSearch" />
      </div>

      <div class="toolbar-right">
        <el-button type="primary" @click="handleCreate">
          <i class="el-icon-plus"></i>
          创建培训任务
        </el-button>
        <el-button @click="handleRefresh">
          <i class="el-icon-refresh"></i>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 培训任务列表 -->
    <el-table :data="trainingList" v-loading="loading" stripe style="width: 100%"
      @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" />

      <el-table-column prop="title" label="培训标题" min-width="200">
        <template slot-scope="scope">
          <div class="title-cell">
            <span class="title-text">{{ scope.row.title }}</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="category" label="分类" min-width="160">
        <template slot-scope="scope">
          <el-tag size="small">{{ trainingCategoryText(scope.row.category) || "未分类" }}</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="estimatedDuration" label="预计时长" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.trainMaterials">
            {{ scope.row.trainMaterials.estimatedDuration }}分钟
          </span>
          <span v-else class="text-muted">线下培训</span>
        </template>
      </el-table-column>

      <el-table-column label="人脸校验" width="110">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.faceVerifyEnabled" size="mini" type="success">
            开启 {{ scope.row.faceVerifyCount || 0 }} 次
          </el-tag>
          <span v-else class="text-muted">关闭</span>
        </template>
      </el-table-column>

      <el-table-column prop="driverCount" label="下发司机" width="100">
        <template slot-scope="scope">
          <el-tag size="mini" type="info"> {{ scope.row.dispatchCount || 0 }}人 </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="driverCount" label="参与司机" width="100">
        <template slot-scope="scope">
          <el-tag size="mini" type="info"> {{ scope.row.totalJoinCount || 0 }}人 </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="完成截止" width="120">
        <template slot-scope="scope">
          <span v-if="scope.row.latestFinishTime">{{ String(scope.row.latestFinishTime).slice(0, 10) }}</span>
          <span v-else class="text-muted">不限</span>
        </template>
      </el-table-column>

      <el-table-column label="过期天数" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.expireDays != null && scope.row.expireDays >= 0">{{ scope.row.expireDays }}天</span>
          <span v-else class="text-muted">不限</span>
        </template>
      </el-table-column>

      <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip>
        <template slot-scope="scope">
          <span v-if="scope.row.remark">{{ scope.row.remark }}</span>
          <span v-else class="text-muted">—</span>
        </template>
      </el-table-column>

      <el-table-column prop="createTime" label="创建时间" width="160">
        <template slot-scope="scope">
          {{ scope.row.createTime }}
        </template>
      </el-table-column>

      <el-table-column label="操作" width="360" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" type="primary" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button size="mini" @click="handleCopy(scope.row)">复制</el-button>
          <el-button size="mini" type="success" @click="handleAssign(scope.row)">分配</el-button>
          <el-button size="mini" type="warning" @click="handleStatistics(scope.row)">统计</el-button>
          <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
        :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pagination.pageSize"
        :total="pagination.total" layout="total, sizes, prev, pager, next, jumper" />
    </div>

    <!-- 新增/编辑对话框 -->
    <TrainingDialog :visible.sync="dialogVisible" :form-data="currentFormData" :mode="dialogMode"
      @confirm="handleDialogConfirm" />

    <!-- 分配司机对话框 -->
    <DriverAssignDialog :visible.sync="assignDialogVisible" :training-data="assignData"
      @confirm="handleAssignConfirm" />

    <!-- 统计对话框 -->
    <TrainingStatisticsDialog :visible.sync="statisticsDialogVisible" :training-data="statisticsData" />
  </div>
</template>

<script>
import TrainingDialog from "./TrainingDialog.vue";
import DriverAssignDialog from "./DriverAssignDialog.vue";
import TrainingStatisticsDialog from "./TrainingStatisticsDialog.vue";
import { getTrainingList, deleteTraining, copyTraining } from "@/api/new_training";
import { ParamUtil } from "@/util";
import trainingCategoryMixin from "../utils/trainingCategory";
export default {
  name: "TrainingManagement",
  mixins: [trainingCategoryMixin],
  components: {
    TrainingDialog,
    DriverAssignDialog,
    TrainingStatisticsDialog
  },
  data() {
    return {
      loading: false,
      trainingList: [],
      selectedRows: [],

      // 搜索表单
      searchForm: {
        title: ""
      },
      createTimeRange: null,

      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },

      // 对话框
      dialogVisible: false,
      dialogMode: "create", // create | edit
      currentFormData: {},

      // 分配对话框
      assignDialogVisible: false,
      assignData: {},

      // 统计对话框
      statisticsDialogVisible: false,
      statisticsData: {}
    };
  },
  created() {
    this.query();
  },
  methods: {
    // 加载培训任务列表（后台分页）
    query(page = 1) {
      this.loading = true;
      const range = this.createTimeRange || [];
      const param = {
        page: {
          current: page,
          size: this.pagination.pageSize
        },
        param: ParamUtil.filterEmptyData(this.searchForm),
        startTime: range[0],
        endTime: range[1]
      };
      getTrainingList(param)
        .then((res) => {
          const { records = [], current = 1, total = 0 } = res.data || {};
          this.trainingList = records;
          this.pagination.currentPage = current;
          this.pagination.total = total;
        })
        .catch((error) => {
          this.$message.error("加载培训任务列表失败");
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 刷新/搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.query(1);
    },

    // 刷新
    handleRefresh() {
      this.query(this.pagination.currentPage);
    },

    // 新增
    handleCreate() {
      this.dialogMode = "create";
      this.currentFormData = {};
      this.dialogVisible = true;
    },

    // 编辑
    handleEdit(row) {
      this.dialogMode = "edit";
      this.currentFormData = { ...row };
      this.dialogVisible = true;
    },

    // 复制任务，不带司机分配
    async handleCopy(row) {
      await copyTraining(row.id);
      this.$message.success("复制成功");
      this.query(1);
    },

    // 分配司机
    handleAssign(row) {
      this.assignData = { ...row };
      this.assignDialogVisible = true;
    },

    // 统计
    handleStatistics(row) {
      this.statisticsData = { ...row };
      this.statisticsDialogVisible = true;
    },

    // 删除
    // eslint-disable-next-line no-unused-vars
    handleDelete(row) {
      this.$confirm("确定要删除这个培训任务吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        await deleteTraining(row.id);
        this.$message.success("删除成功");
        this.query(this.pagination.currentPage);
      });
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },

    // 分页变化
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.query(1);
    },

    handleCurrentChange(page) {
      this.query(page);
    },

    // 对话框确认
    handleDialogConfirm(formData) {
      if (this.dialogMode === "create") {
        this.$message.success("创建成功");
        this.query(1); // 新增后跳转到第一页
      } else {
        this.$message.success("编辑成功");
        this.query(this.pagination.currentPage); // 编辑后刷新当前页
      }
      this.$emit("training-created", formData);
    },

    // 分配确认
    handleAssignConfirm() {
      this.$message.success("分配成功");
      this.query(this.pagination.currentPage); // 分配后刷新当前页
    },

    // 显示创建对话框
    showCreateDialog() {
      this.handleCreate();
    },

    // 刷新列表
    refreshList() {
      this.query(this.pagination.currentPage);
    }
  }
};
</script>

<style lang="less" scoped>
.training-management {
  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 6px;

    .toolbar-left {
      display: flex;
      align-items: center;
    }

    .toolbar-right {
      .el-button {
        margin-left: 8px;
      }
    }
  }

  .title-cell {
    display: flex;
    align-items: center;

    .title-text {
      font-weight: 500;
    }
  }

  .pagination-container {
    margin-top: 20px;
    text-align: right;
  }

  .text-muted {
    color: #909399;
  }
}
</style>
